import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash2, RefreshCw, Loader2, Wind, Heart, Sparkles, PenLine, Lightbulb, Clock, ArrowLeft, Image as ImageIcon, Edit2, BookOpen, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

const PROMPT_LIBRARY = {
  reflection: [
    '¿Qué momento de hoy quisieras revivir y por qué?',
    'Si pudieras enviar un mensaje a tu yo de hace un año, ¿qué le dirías?',
    '¿Qué creencia tuya cambió en los últimos meses?',
    '¿Qué pequeño detalle hizo tu día más bonito?',
    'Describe una emoción que sentiste hoy con una metáfora',
    '¿Qué decisión tomaste hoy de la que te sientes orgullosa?',
    '¿Qué te dice tu intuición sobre algo que estás postergando?',
    '¿En qué área de tu vida sientes que estás creciendo?',
    '¿Qué cosa nueva aprendiste sobre ti esta semana?',
    '¿Cuál fue el aprendizaje más valioso del último mes?',
    '¿Qué conversación te dejó pensando últimamente?',
    '¿Qué aspecto de tu rutina actual quieres cambiar y por qué?',
    '¿Qué pequeña victoria celebraste hoy aunque nadie la notara?',
    '¿Qué parte de tu pasado te sigue definiendo?',
    '¿Qué versión de ti estás construyendo ahora mismo?',
    '¿Qué señal te está enviando tu cuerpo que aún no escuchas?',
    '¿Qué historia te cuentas sobre ti que quizás ya no es cierta?',
  ],
  gratitude: [
    'Tres cosas pequeñas por las que doy gracias hoy son...',
    'Una persona que aprecié recientemente y por qué',
    '¿Qué parte de tu cuerpo agradeces hoy y por qué?',
    'Un lujo cotidiano que doy por sentado pero amo',
    'Una habilidad que tengo que me hace sentir con suerte',
    '¿Qué aprendí hoy que vale oro?',
    'Una dificultad que me enseñó algo importante',
    'Un lugar al que puedo ir que me hace sentir en casa',
    'Una comida simple que me hace feliz',
    'Alguien que estuvo para mí sin que se lo pidiera',
    'Un privilegio que tengo y quiero reconocer hoy',
    'Algo de la naturaleza que hoy me tocó',
    'Un sonido que me devuelve al presente',
    'Un recuerdo al que vuelvo cuando necesito calma',
  ],
  intention: [
    '¿Qué quiero crear esta semana?',
    'Mi mejor versión de mañana se ve como...',
    'Algo que quiero soltar para abrir espacio nuevo',
    '¿Qué necesita más mi atención esta temporada?',
    'Una promesa que me quiero hacer hoy',
    '¿Qué palabra quiero que guíe mis próximos 7 días?',
    '¿Qué hábito quiero fortalecer este mes?',
    '¿A quién quiero volverme antes de fin de año?',
    '¿Qué proyecto merece que le dedique tiempo esta semana?',
    '¿Qué límite saludable quiero poner y con quién?',
    '¿Qué conversación pendiente quiero iniciar pronto?',
    '¿A qué quiero decirle sí y a qué quiero decirle no?',
    '¿Qué espacio físico quiero cuidar mejor?',
  ],
  concerns: [
    '¿Qué me preocupa hoy y qué parte depende de mí?',
    '¿Qué patrón sigo repitiendo aunque sé que no me sirve?',
    '¿Qué emoción evito sentir? ¿Qué intenta decirme?',
    '¿De qué tengo miedo y por qué tiene poder sobre mí?',
    '¿Qué expectativas externas estoy cargando que no son mías?',
    '¿Qué necesito perdonarme?',
    '¿Qué conversación estoy evitando y por qué?',
    '¿Qué escenario catastrófico imagino con frecuencia? ¿Qué tan probable es?',
    '¿Qué me está robando energía esta semana?',
    '¿Qué ruido interno no me deja dormir bien últimamente?',
    '¿Qué estoy callando por miedo a incomodar?',
    '¿Qué tengo postergado que me está generando ansiedad?',
    '¿De qué me estoy haciendo cargo que no me corresponde?',
    '¿Qué parte de mi día siento que vivo en automático?',
    '¿Qué tipo de ayuda necesito pero me cuesta pedir?',
  ],
  creative: [
    'Si hoy fuera un color y un sonido, sería...',
    'Escribe el primer párrafo de tu autobiografía',
    'Inventa una historia con: un farol, una carta y la lluvia',
    'Si tu vida fuera una película, ¿qué escena tocaría ahora?',
    'Describe tu lugar favorito como si fuera un personaje',
    'Si pudieras diseñar tu día perfecto minuto a minuto, ¿cómo sería?',
    'Escribe una carta a la ciudad donde vives',
    'Tu niñez como una receta: ingredientes y preparación',
    'Un objeto cotidiano contando su día desde su perspectiva',
    'Describe el olor de tu casa en tres oraciones',
    'Una canción que sea un recuerdo entero',
    'Tu futuro yo te escribe: ¿qué te cuenta?',
  ],
};

// Cover presets are derived from the active theme. Each theme produces 8 variations:
// 4 gradient combinations across primary/accent/card/bg + 4 subtler monochromatic options.
const buildCoverPresets = (theme) => [
  { id: 'theme-1', name: 'Aurora', bg: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` },
  { id: 'theme-2', name: 'Horizon', bg: `linear-gradient(180deg, ${theme.primary}, ${theme.card})` },
  { id: 'theme-3', name: 'Ember', bg: `linear-gradient(135deg, ${theme.accent}, ${theme.primaryHover || theme.primary})` },
  { id: 'theme-4', name: 'Misty', bg: `linear-gradient(135deg, ${theme.card}, ${theme.accent}30, ${theme.primary}50)` },
  { id: 'theme-5', name: 'Eclipse', bg: `linear-gradient(135deg, ${theme.bg}, ${theme.elevated}, ${theme.primary}80)` },
  { id: 'theme-6', name: 'Golden Hour', bg: `linear-gradient(225deg, ${theme.accent}, ${theme.light || theme.accent}, ${theme.primary})` },
  { id: 'theme-7', name: 'Soft', bg: `linear-gradient(180deg, ${theme.elevated}, ${theme.card}, ${theme.primary}30)` },
  { id: 'theme-8', name: 'Deep', bg: `linear-gradient(135deg, ${theme.primary}CC, ${theme.bg})` },
];
// Kept for backward compatibility with existing book records whose cover_value begins with 'gradient-' or 'pattern-':
const COVER_PRESETS = [
  { id: 'gradient-1', name: 'Sunset', bg: 'linear-gradient(135deg, #FA709A, #FEE140)' },
  { id: 'gradient-2', name: 'Ocean', bg: 'linear-gradient(135deg, #4FACFE, #00F2FE)' },
  { id: 'gradient-3', name: 'Lavender', bg: 'linear-gradient(135deg, #A78BFA, #EC4899)' },
  { id: 'gradient-4', name: 'Forest', bg: 'linear-gradient(135deg, #43E97B, #38F9D7)' },
  { id: 'gradient-5', name: 'Plum', bg: 'linear-gradient(135deg, #667EEA, #764BA2)' },
  { id: 'gradient-6', name: 'Coral', bg: 'linear-gradient(135deg, #F093FB, #F5576C)' },
  { id: 'gradient-7', name: 'Gold', bg: 'linear-gradient(135deg, #FAD961, #F76B1C)' },
  { id: 'gradient-8', name: 'Mint', bg: 'linear-gradient(135deg, #A8EDEA, #FED6E3)' },
];

const BREATH_PATTERNS = [
  { name: 'Caja 4-4-4-4', desc: 'Inhalar 4s · sostener 4s · exhalar 4s · sostener 4s', steps: [['Inhala', 4], ['Mantén', 4], ['Exhala', 4], ['Mantén', 4]] },
  { name: '4-7-8 calma', desc: 'Inhalar 4s · sostener 7s · exhalar 8s', steps: [['Inhala', 4], ['Mantén', 7], ['Exhala', 8]] },
  { name: 'Respiración profunda', desc: 'Inhalar 5s · exhalar 7s', steps: [['Inhala', 5], ['Exhala', 7]] },
];

const MOODS = [
  { e: '😄', label: 'Genial' }, { e: '🙂', label: 'Bien' }, { e: '😐', label: 'Neutral' },
  { e: '😟', label: 'Inquieto' }, { e: '😢', label: 'Triste' }, { e: '😴', label: 'Cansado' },
  { e: '🔥', label: 'Energía' }, { e: '💭', label: 'Pensativo' },
];

const BookCover = ({ book, onClick, theme, large = false }) => {
  const themePresets = buildCoverPresets(theme);
  const preset = themePresets.find(p => p.id === book.cover_value) || COVER_PRESETS.find(p => p.id === book.cover_value);
  const isImage = book.cover_type === 'image' && book.cover_value?.startsWith('http');
  return (
    <button onClick={onClick} className={`relative rounded-xl overflow-hidden shadow-lg transition-all hover:scale-[1.03] hover:shadow-2xl group ${large ? '' : ''}`}
      style={{ aspectRatio: '3/4', width: '100%' }}>
      {isImage ? (
        <img src={book.cover_value} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full" style={{
          background: preset?.bg || `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
          backgroundSize: preset?.size || 'auto',
        }} />
      )}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7) 100%)' }} />
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white text-sm font-semibold drop-shadow-lg truncate">{book.name}</p>
        {book.description && <p className="text-white/80 text-[10px] truncate">{book.description}</p>}
      </div>
      <div className="absolute top-2 right-2 bg-black/40 backdrop-blur-md px-2 py-0.5 rounded-full text-[10px] text-white">
        {book.entries_count || 0} entradas
      </div>
    </button>
  );
};

const Journal = () => {
  const [books, setBooks] = useState([]);
  const [activeBook, setActiveBook] = useState(null);
  const [entries, setEntries] = useState([]);
  const [view, setView] = useState('shelf'); // shelf | book
  const [showNewBook, setShowNewBook] = useState(false);
  const [newBookName, setNewBookName] = useState('');
  const [newBookDesc, setNewBookDesc] = useState('');
  const [newBookCover, setNewBookCover] = useState('theme-1');
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [newBookCoverImg, setNewBookCoverImg] = useState('');
  const [editingBook, setEditingBook] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({ title: '', content: '', mood: '', prompt: '', type: 'text', image_url: '' });
  const [promptCategory, setPromptCategory] = useState('reflection');
  const [currentPrompts, setCurrentPrompts] = useState([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [breathing, setBreathing] = useState(null);
  const { theme } = useTheme();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBooks(); shufflePrompts('reflection'); }, []);
  const fetchBooks = async () => {
    try {
      const { data } = await api.get('/journal-books');
      // count entries per book
      const all = (await api.get('/journal')).data;
      const counts = {};
      all.forEach(e => { counts[e.book_id || 'default'] = (counts[e.book_id || 'default'] || 0) + 1; });
      setBooks(data.map(b => ({ ...b, entries_count: counts[b.id] || 0 })));
    } catch (err) { console.debug(err); }
  };
  const fetchEntries = async (bookId) => { try { setEntries((await api.get(`/journal?book_id=${bookId}`)).data); } catch (err) { console.debug(err); } };

  // Track shown prompts per category to ensure shuffle returns unseen questions.
  const [seenPrompts] = useState(() => ({ reflection: new Set(), gratitude: new Set(), intention: new Set(), concerns: new Set(), creative: new Set() }));
  const shufflePrompts = (cat) => {
    setPromptCategory(cat);
    const lib = PROMPT_LIBRARY[cat] || [];
    const seen = seenPrompts[cat];
    // If we already saw all prompts, reset the seen set to start fresh.
    if (seen.size >= lib.length) seen.clear();
    const available = lib.filter(p => !seen.has(p));
    const shuffled = [...available].sort(() => 0.5 - Math.random()).slice(0, 4);
    shuffled.forEach(p => seen.add(p));
    setCurrentPrompts(shuffled);
  };

  const createBook = async () => {
    if (!newBookName.trim()) { toast.error('Nombre requerido'); return; }
    const isImage = newBookCoverImg.length > 0;
    try {
      await api.post('/journal-books', {
        name: newBookName, description: newBookDesc,
        cover_type: isImage ? 'image' : 'preset',
        cover_value: isImage ? newBookCoverImg : newBookCover,
      });
      toast.success('Diario creado');
      setShowNewBook(false); setNewBookName(''); setNewBookDesc(''); setNewBookCover('theme-1'); setNewBookCoverImg('');
      fetchBooks();
    } catch { toast.error('Error'); }
  };

  const updateBook = async () => {
    if (!editingBook) return;
    try {
      await api.put(`/journal-books/${editingBook.id}`, {
        name: editingBook.name, description: editingBook.description,
        cover_type: editingBook.cover_type, cover_value: editingBook.cover_value,
      });
      toast.success('Actualizado'); setEditingBook(null); fetchBooks();
    } catch (err) { console.debug(err); }
  };

  const deleteBook = async (id) => {
    if (!window.confirm('¿Eliminar este diario y todas sus entradas?')) return;
    try { await api.delete(`/journal-books/${id}`); fetchBooks(); } catch (err) { console.debug(err); }
  };

  const openBook = async (book) => {
    setActiveBook(book); await fetchEntries(book.id); setView('book');
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewBookCoverImg(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success('Portada lista');
    } catch { toast.error('Error al subir'); }
  };

  const handleEntryImageUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewEntry({ ...newEntry, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, type: 'image' });
      toast.success('Imagen lista');
    } catch { toast.error('Error al subir'); }
  };

  const startWith = (prompt) => {
    setNewEntry({ title: prompt.slice(0, 60), content: '', mood: '', prompt, type: 'text', image_url: '' });
    setShowAddDialog(true);
  };

  const addEntry = async () => {
    if (newEntry.type === 'text' && !newEntry.content.trim()) { toast.error('Escribe algo'); return; }
    if (newEntry.type === 'image' && !newEntry.image_url) { toast.error('Sube una imagen'); return; }
    try {
      const t = newEntry.title || `Entrada ${new Date().toLocaleDateString('es-ES')}`;
      const body = {
        title: t, content: (newEntry.mood ? `[${newEntry.mood}] ` : '') + (newEntry.prompt ? `\n\n💭 ${newEntry.prompt}\n\n` : '') + newEntry.content,
        book_id: activeBook?.id || null, type: newEntry.type, image_url: newEntry.image_url || null,
      };
      await api.post('/journal', body);
      toast.success('Guardado');
      setNewEntry({ title: '', content: '', mood: '', prompt: '', type: 'text', image_url: '' });
      setShowAddDialog(false);
      if (activeBook) fetchEntries(activeBook.id);
      fetchBooks();
    } catch { toast.error('Error'); }
  };

  const deleteEntry = async (id) => {
    try { await api.delete(`/journal/${id}`); if (activeBook) fetchEntries(activeBook.id); fetchBooks(); } catch (err) { console.debug(err); }
  };

  const askAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true); setAiResponse('');
    try {
      const { data } = await api.post('/ai/journal-reflect', { prompt: aiPrompt });
      setAiResponse(data.response || '');
    } catch { toast.error('Error de IA'); }
    finally { setAiLoading(false); }
  };

  const startBreathing = (p) => setBreathing({ pattern: p, stepIndex: 0, secondsLeft: p.steps[0][1], cycle: 1 });
  useEffect(() => {
    if (!breathing) return;
    const t = setTimeout(() => {
      setBreathing(b => {
        if (!b) return null;
        if (b.secondsLeft > 1) return { ...b, secondsLeft: b.secondsLeft - 1 };
        const nextIdx = (b.stepIndex + 1) % b.pattern.steps.length;
        const nextCycle = nextIdx === 0 ? b.cycle + 1 : b.cycle;
        if (nextCycle > 5) return null;
        return { ...b, stepIndex: nextIdx, secondsLeft: b.pattern.steps[nextIdx][1], cycle: nextCycle };
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [breathing]);

  const inputStyle = { background: theme.elevated, borderColor: theme.border };

  // ==== SHELF VIEW ====
  if (view === 'shelf') {
    return (
      <div data-testid="journal-page" className="space-y-6">
        <div className="relative overflow-hidden rounded-3xl p-6 md:p-10"
          style={{ background: theme.gradient || `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, border: `1px solid ${theme.primary}40` }}>
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, ${theme.bg}cc 100%)` }} />
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: theme.accent }} />
          <div className="relative z-10 flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-5xl sm:text-7xl font-light tracking-tighter" style={{ color: theme.textPrimary }}>Diario</h1>
            </div>
            <Button data-testid="new-book-btn" onClick={() => setShowNewBook(true)} className="text-white" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              <Plus size={16} className="mr-1" />Nuevo diario
            </Button>
          </div>
        </div>

        {/* Books grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {books.map(b => (
            <div key={b.id} className="relative group" data-testid={`book-${b.id}`}>
              <BookCover book={b} onClick={() => openBook(b)} theme={theme} />
              <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button onClick={(e) => { e.stopPropagation(); setEditingBook(b); }} className="p-1.5 rounded-full bg-black/60 backdrop-blur text-white"><Edit2 size={11} /></button>
                <button onClick={(e) => { e.stopPropagation(); deleteBook(b.id); }} className="p-1.5 rounded-full bg-red-600/80 backdrop-blur text-white"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
          {books.length === 0 && (
            <div className="col-span-full text-center py-20 rounded-xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <BookOpen size={48} className="mx-auto mb-3" style={{ color: theme.textMuted }} />
              <p style={{ color: theme.textMuted }}>Crea tu primer diario para comenzar</p>
            </div>
          )}
        </div>

        {/* New Book Dialog */}
        <Dialog open={showNewBook} onOpenChange={setShowNewBook}>
          <DialogContent className="max-w-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <DialogHeader><DialogTitle style={{ color: theme.accent }}>Nuevo diario</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Nombre (Sueños, Viaje 2026, Gratitud...)" value={newBookName} onChange={(e) => setNewBookName(e.target.value)} className="text-white" style={inputStyle} />
              <Input placeholder="Descripción corta (opcional)" value={newBookDesc} onChange={(e) => setNewBookDesc(e.target.value)} className="text-white" style={inputStyle} />
              <div>
                <p className="text-xs mb-2" style={{ color: theme.textMuted }}>Portada</p>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {buildCoverPresets(theme).map(p => (
                    <button key={p.id} onClick={() => { setNewBookCover(p.id); setNewBookCoverImg(''); }}
                      className="rounded-md overflow-hidden hover:scale-105 transition-transform"
                      style={{ aspectRatio: '3/4', background: p.bg, backgroundSize: p.size || 'auto', border: newBookCover === p.id && !newBookCoverImg ? `2px solid ${theme.accent}` : '2px solid transparent' }}
                      title={p.name} />
                  ))}
                  <label htmlFor="cover-upload" className="rounded-md flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
                    style={{ aspectRatio: '3/4', background: theme.elevated, border: newBookCoverImg ? `2px solid ${theme.accent}` : `2px dashed ${theme.border}` }}>
                    {newBookCoverImg ? <img src={newBookCoverImg} alt="" className="w-full h-full object-cover rounded-md" /> : <ImageIcon size={20} style={{ color: theme.textMuted }} />}
                  </label>
                  <input id="cover-upload" type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                </div>
              </div>
              <Button data-testid="create-book-btn" onClick={createBook} className="w-full text-white" style={{ background: theme.primary }}>Crear diario</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit book dialog */}
        <Dialog open={!!editingBook} onOpenChange={(o) => !o && setEditingBook(null)}>
          <DialogContent className="max-w-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <DialogHeader><DialogTitle style={{ color: theme.accent }}>Editar diario</DialogTitle></DialogHeader>
            {editingBook && (
              <div className="space-y-4">
                <Input value={editingBook.name} onChange={(e) => setEditingBook({ ...editingBook, name: e.target.value })} className="text-white" style={inputStyle} />
                <Input placeholder="Descripción" value={editingBook.description || ''} onChange={(e) => setEditingBook({ ...editingBook, description: e.target.value })} className="text-white" style={inputStyle} />
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {buildCoverPresets(theme).map(p => (
                    <button key={p.id} onClick={() => setEditingBook({ ...editingBook, cover_type: 'preset', cover_value: p.id })}
                      className="rounded-md overflow-hidden hover:scale-105 transition-transform"
                      style={{ aspectRatio: '3/4', background: p.bg, backgroundSize: p.size || 'auto', border: editingBook.cover_value === p.id ? `2px solid ${theme.accent}` : '2px solid transparent' }} />
                  ))}
                </div>
                <Button onClick={updateBook} className="w-full text-white" style={{ background: theme.primary }}>Guardar</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==== BOOK VIEW (single journal book) ====
  return (
    <div data-testid="journal-book-view" className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => setView('shelf')} className="p-2 rounded-md" style={{ background: theme.card, border: `1px solid ${theme.border}`, color: theme.accent }}><ArrowLeft size={16} /></button>
        <div className="flex-1">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tighter" style={{ color: theme.accent }}>{activeBook.name}</h1>
          {activeBook.description && <p className="text-xs mt-1" style={{ color: theme.textMuted }}>{activeBook.description}</p>}
        </div>
        <Button onClick={() => { setNewEntry({ title: '', content: '', mood: '', prompt: '', type: 'text', image_url: '' }); setShowAddDialog(true); }}
          className="text-white" style={{ background: theme.primary }}>
          <Plus size={16} className="mr-1" />Entrada
        </Button>
      </div>

      <Tabs defaultValue="entries" className="w-full">
        <TabsList style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <TabsTrigger value="entries">Entradas ({entries.length})</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="mindfulness">Mindfulness</TabsTrigger>
          <TabsTrigger value="ai">Reflexión IA</TabsTrigger>
        </TabsList>

        <TabsContent value="entries" className="mt-4 space-y-3">
          {entries.length === 0 && (
            <div className="text-center py-16 rounded-xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <PenLine size={32} className="mx-auto mb-2" style={{ color: theme.textMuted }} />
              <p style={{ color: theme.textMuted }}>Este diario aún está vacío</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {entries.map(entry => (
              <div key={entry.id} className="rounded-xl overflow-hidden group" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
                {entry.type === 'image' && entry.image_url && (
                  <button onClick={() => setFullscreenImage(entry.image_url)} className="block w-full focus:outline-none">
                    <img src={entry.image_url} alt="" className="w-full h-64 object-cover cursor-zoom-in hover:opacity-90 transition-opacity" />
                  </button>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium" style={{ color: theme.accent }}>{entry.title}</h3>
                      <p className="text-[10px] flex items-center gap-1 mt-0.5" style={{ color: theme.textMuted }}><Clock size={10} />{new Date(entry.created_at).toLocaleString('es-ES')}</p>
                    </div>
                    <button onClick={() => deleteEntry(entry.id)} className="opacity-0 group-hover:opacity-100" style={{ color: theme.error }}><Trash2 size={14} /></button>
                  </div>
                  {entry.content && <p className="text-sm whitespace-pre-line mt-3" style={{ color: theme.textSecondary }}>{entry.content}</p>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="prompts" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {[{ id: 'reflection', label: 'Reflexión', icon: Sparkles }, { id: 'gratitude', label: 'Gratitud', icon: Heart }, { id: 'intention', label: 'Intención', icon: Lightbulb }, { id: 'concerns', label: 'Preocupaciones', icon: Wind }, { id: 'creative', label: 'Creativo', icon: PenLine }].map(c => {
              const Icon = c.icon; const active = promptCategory === c.id;
              return (
                <button key={c.id} onClick={() => shufflePrompts(c.id)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs"
                  style={{ background: active ? theme.primary : theme.card, color: active ? '#fff' : theme.textSecondary, border: `1px solid ${active ? theme.primary : theme.border}` }}>
                  <Icon size={12} />{c.label}
                </button>
              );
            })}
            <button onClick={() => shufflePrompts(promptCategory)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs" style={{ background: theme.elevated, color: theme.textMuted, border: `1px solid ${theme.border}` }}>
              <RefreshCw size={12} />Otras
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentPrompts.map((p, i) => (
              <button key={i} onClick={() => startWith(p)} className="text-left p-6 rounded-xl hover:scale-[1.01] group"
                style={{ background: `linear-gradient(135deg, ${theme.card}, ${theme.elevated})`, border: `1px solid ${theme.border}` }}>
                <div className="flex items-start gap-3">
                  <span className="text-3xl opacity-60 group-hover:opacity-100" style={{ color: theme.accent }}>"</span>
                  <p className="text-sm font-light leading-relaxed" style={{ color: theme.textPrimary }}>{p}</p>
                </div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="mindfulness" className="mt-4 space-y-4">
          {breathing ? (
            <div className="rounded-2xl p-12 text-center" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: theme.textMuted }}>{breathing.pattern.name} · ciclo {breathing.cycle}/5</p>
              <div className="relative mx-auto w-48 h-48 my-6">
                <div className="absolute inset-0 rounded-full transition-all duration-1000"
                  style={{ background: `radial-gradient(circle, ${theme.primary}40, transparent)`, transform: `scale(${breathing.pattern.steps[breathing.stepIndex][0] === 'Inhala' ? 1.4 : breathing.pattern.steps[breathing.stepIndex][0] === 'Exhala' ? 0.7 : 1.05})` }} />
                <div className="absolute inset-4 rounded-full flex flex-col items-center justify-center" style={{ background: theme.elevated, border: `2px solid ${theme.accent}` }}>
                  <p className="text-3xl font-light" style={{ color: theme.accent }}>{breathing.pattern.steps[breathing.stepIndex][0]}</p>
                  <p className="text-5xl font-light mt-1" style={{ color: theme.textPrimary }}>{breathing.secondsLeft}</p>
                </div>
              </div>
              <Button onClick={() => setBreathing(null)} variant="outline" style={{ borderColor: theme.border, color: theme.textSecondary }}>Detener</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {BREATH_PATTERNS.map((p, i) => (
                <button key={i} onClick={() => startBreathing(p)} className="text-left p-6 rounded-xl hover:scale-[1.01]" style={{ background: `linear-gradient(135deg, ${theme.primary}15, ${theme.card})`, border: `1px solid ${theme.border}` }}>
                  <Wind size={20} style={{ color: theme.accent }} />
                  <p className="text-base font-medium mt-2" style={{ color: theme.textPrimary }}>{p.name}</p>
                  <p className="text-xs mt-1" style={{ color: theme.textMuted }}>{p.desc}</p>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <div className="rounded-xl p-6" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Cuenta cómo te sientes. La IA te devolverá una reflexión amable.</p>
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Hoy me siento..." className="min-h-[100px] text-white" style={inputStyle} />
            <Button onClick={askAI} disabled={aiLoading} className="mt-3 text-white" style={{ background: theme.primary }}>
              {aiLoading ? <><Loader2 size={16} className="mr-1 animate-spin" />Pensando...</> : <><Sparkles size={16} className="mr-1" />Reflexionar</>}
            </Button>
            {aiResponse && (
              <div className="mt-4 p-4 rounded-lg" style={{ background: theme.elevated, borderLeft: `3px solid ${theme.accent}` }}>
                <p className="text-sm whitespace-pre-line" style={{ color: theme.textPrimary }}>{aiResponse}</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Compose dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle style={{ color: theme.accent }}>Nueva entrada en {activeBook.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {newEntry.prompt && (
              <div className="p-3 rounded-lg" style={{ background: theme.elevated, borderLeft: `3px solid ${theme.accent}` }}>
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.accent }}>Prompt</p>
                <p className="text-sm italic" style={{ color: theme.textSecondary }}>"{newEntry.prompt}"</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setNewEntry({ ...newEntry, type: 'text' })} variant="outline"
                style={{ background: newEntry.type === 'text' ? theme.primary : 'transparent', color: newEntry.type === 'text' ? '#fff' : theme.textMuted, border: `1px solid ${theme.border}` }}>
                <PenLine size={14} className="mr-1" />Texto
              </Button>
              <Button onClick={() => setNewEntry({ ...newEntry, type: 'image' })} variant="outline"
                style={{ background: newEntry.type === 'image' ? theme.primary : 'transparent', color: newEntry.type === 'image' ? '#fff' : theme.textMuted, border: `1px solid ${theme.border}` }}>
                <ImageIcon size={14} className="mr-1" />Imagen
              </Button>
            </div>
            <Input placeholder="Título" value={newEntry.title} onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })} className="text-white" style={inputStyle} />
            {newEntry.type === 'image' && (
              <div>
                <label htmlFor="entry-img" className="flex items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer" style={{ borderColor: theme.border }}>
                  <ImageIcon size={24} style={{ color: theme.accent }} />
                  <span style={{ color: theme.textMuted }}>{newEntry.image_url ? 'Cambiar imagen' : 'Subir imagen'}</span>
                </label>
                <input id="entry-img" type="file" accept="image/*" onChange={handleEntryImageUpload} className="hidden" />
                {newEntry.image_url && <img src={newEntry.image_url} alt="" className="mt-2 max-h-48 rounded-lg" />}
              </div>
            )}
            <div>
              <p className="text-xs mb-2" style={{ color: theme.textMuted }}>¿Cómo te sientes?</p>
              <div className="flex gap-1.5 flex-wrap">
                {MOODS.map(m => (
                  <button key={m.e} onClick={() => setNewEntry({ ...newEntry, mood: m.e })}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
                    style={{ background: newEntry.mood === m.e ? theme.primary : theme.elevated, color: newEntry.mood === m.e ? '#fff' : theme.textMuted, border: `1px solid ${theme.border}` }}>
                    <span className="text-base">{m.e}</span>{m.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea placeholder="Escribe lo que sientas..." value={newEntry.content} onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
              className="min-h-[180px] text-white text-base leading-relaxed" style={inputStyle} />
            <Button onClick={addEntry} className="w-full text-white" style={{ background: theme.primary }}>Guardar entrada</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen image viewer */}
      {fullscreenImage && (
        <div onClick={() => setFullscreenImage(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)' }}>
          <button onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)' }}
            data-testid="journal-fullscreen-close">
            <X size={20} />
          </button>
          <img src={fullscreenImage} alt="" onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default" />
        </div>
      )}
    </div>
  );
};

export default Journal;
